import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';

import ItemBulkUploadReviewTable from 'src/components/agile/agileComponents/ItemBulkUploadOperation/ItemBulkUploadReviewTable';
import ItemBulkUploadDiffTable from 'src/components/agile/agileComponents/ItemBulkUploadOperation/ItemBulkUploadDiffTable';
import { ItemEntryInput } from '@components/agile/types/ItemEntryTypes';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import { getActionPublisherForProgram, publishKatalMetric } from 'src/metrics/publishers';
import {
  METHOD_NAME_BULK_UPLOAD,
  METRIC_NAME_GENERATE_DIFF,
  METRIC_NAME_GO_TO_BULK_UPLOAD,
  MetricType,
} from 'src/constants/metricConstants';
import useGetPlanDiff from 'src/store/hooks/useGetPlanDiff';
import { ItemType } from '@graphql/generated/graphql';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';

export interface ItemBulkUploadMainProps {
  listOfMacroplanogram: AgileMacroplanogram[] | undefined | null;
  swapTime: number;
  selectedMacroplanogramId: string | undefined;
  inputItemEntries: ItemEntryInput[];
  inputTableFlag: boolean;
  setInputTableFlag: (value: boolean) => void;
  reviewTableFlag: boolean;
  setReviewTableFlag: (value: boolean) => void;
  alertFlag: boolean;
  setAlertFlag: (value: boolean) => void;
  macroplanogramEndTime: number;
  onSaveSuccess?: () => void;
  onDuplicatesRemoved?: (count: number) => void;
}

const ItemBulkUploadMain = (props: ItemBulkUploadMainProps) => {
  const {
    listOfMacroplanogram,
    swapTime,
    selectedMacroplanogramId,
    inputItemEntries,
    inputTableFlag,
    setInputTableFlag,
    reviewTableFlag,
    setReviewTableFlag,
    alertFlag,
    setAlertFlag,
    macroplanogramEndTime,
    onSaveSuccess,
    onDuplicatesRemoved,
  } = props;
  const queryClient = useQueryClient();
  const config = useProgramConfig();
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const [validatedItemEntries, setValidatedItemEntries] = useState<ItemEntryInput[]>([]);
  const [zoneNotFoundAlertFlag, setZoneNotFoundAlertFlag] = useState<boolean>(false);

  // Clear validated entries when a new file is uploaded so stale data doesn't persist.
  useEffect(() => {
    setValidatedItemEntries([]);
  }, [inputItemEntries]);

  // Get selected macroplanogram
  const selectedMacroplanogram = listOfMacroplanogram?.find((m) => m.entryId === selectedMacroplanogramId);

  // Get subcategories for the selected zone only
  const availableSubcategories = selectedMacroplanogram?.storeSubcategories || [];

  // Macroplanogram-level categoryId (Phase 1 GFC). When set, plan rows carry
  // it as the scope identifier instead of subcategoryId.
  const macroCategoryId = scope === 'category' ? (selectedMacroplanogram?.categoryId ?? undefined) : undefined;

  // Build a name → ID lookup for subcategory translation
  const subcategoryNameToId = new Map(
    availableSubcategories.map((sc) => [sc.subcategoryName.toLowerCase(), sc.subcategoryId]),
  );

  // Prepare GetPlanDiff input using validated entries
  const planDiffInput =
    selectedMacroplanogram && validatedItemEntries.length > 0
      ? {
          storeId: selectedMacroplanogram.storeId,
          macroplanogramId: selectedMacroplanogram.entryId,
          startTime: swapTime,
          plan: validatedItemEntries.map((entry) => ({
            itemId: entry.itemId,
            itemType: entry.itemType.toUpperCase() as ItemType,
            subcategoryId:
              scope === 'subcategory'
                ? subcategoryNameToId.get(entry.subcategory.toLowerCase()) || entry.subcategory
                : undefined,
            categoryId: macroCategoryId,
          })),
        }
      : null;

  // Call GetPlanDiff hook
  const planDiffEnabled = inputTableFlag && !!planDiffInput;
  const { result, isLoading, isError, isSuccess } = useGetPlanDiff(planDiffInput!, planDiffEnabled);

  useEffect(() => {
    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_GO_TO_BULK_UPLOAD,
      1,
      MetricType.Counter,
    );
  }, []);

  useEffect(() => {
    const isListOfMacroplanogramEmpty = Array.isArray(listOfMacroplanogram) && listOfMacroplanogram.length === 0;
    setInputTableFlag(false);
    setZoneNotFoundAlertFlag(isListOfMacroplanogramEmpty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfMacroplanogram]);

  const handleReviewConfirm = (validatedEntries: ItemEntryInput[]) => {
    // Reset diff state so a fresh query fires
    setInputTableFlag(false);
    setValidatedItemEntries(validatedEntries);
    setReviewTableFlag(false);

    if (!selectedMacroplanogramId) {
      setAlertFlag(true);
      return;
    }

    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_GENERATE_DIFF,
      1,
      MetricType.Counter,
    );
  };

  // Show diff results when validated entries are ready
  useEffect(() => {
    if (validatedItemEntries.length > 0 && selectedMacroplanogramId) {
      setInputTableFlag(true);
      // Invalidate any cached plan diff so the query re-fetches fresh data
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'GetPlanDiff' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validatedItemEntries, selectedMacroplanogramId]);

  const handleReviewCancel = () => {
    setReviewTableFlag(false);
    setInputTableFlag(false);
    setValidatedItemEntries([]);
  };

  const handleBackToReview = () => {
    setInputTableFlag(false);
    setReviewTableFlag(true);
  };

  return (
    <>
      {alertFlag && (
        <Alert type="error" onClose={() => setAlertFlag(false)}>
          Please select a macroplanogram before continuing.
        </Alert>
      )}
      {zoneNotFoundAlertFlag && (
        <Alert type="error" onClose={() => setZoneNotFoundAlertFlag(false)}>
          The selected store does not contain any zones for the chosen date. Please create zones before proceeding.
        </Alert>
      )}

      {reviewTableFlag && (
        <ItemBulkUploadReviewTable
          inputItemEntries={validatedItemEntries.length > 0 ? validatedItemEntries : inputItemEntries}
          availableSubcategories={availableSubcategories}
          storeId={selectedMacroplanogram?.storeId ?? ''}
          macroplanogramId={selectedMacroplanogram?.entryId ?? ''}
          onConfirm={handleReviewConfirm}
          onCancel={handleReviewCancel}
          onDuplicatesRemoved={onDuplicatesRemoved}
        />
      )}

      {inputTableFlag && (
        <div>
          {isLoading && (
            <Column alignmentHorizontal="center" alignmentVertical="center" spacing="800" spacingInset="500">
              <Loader type="circular" size="large" />
            </Column>
          )}
          {isError && (
            <Alert type="error">
              An error occurred while fetching the plan diff data. Please check the input and try again.
            </Alert>
          )}
          {isSuccess && (
            <ItemBulkUploadDiffTable
              diffResult={result}
              subcategories={availableSubcategories}
              storeId={selectedMacroplanogram!.storeId}
              macroplanogramId={selectedMacroplanogram!.entryId}
              macroplanogramEndTime={macroplanogramEndTime}
              swapTime={swapTime}
              onBack={handleBackToReview}
              onSaveSuccess={onSaveSuccess}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ItemBulkUploadMain;
